import type { Field, Step } from '@blooq/flow'
import { useCallback, useMemo, useState } from 'react'

import type { StepView } from '../../lib/api'
import {
  askOf,
  canLeave,
  echoOf,
  firstError,
  nextIndex,
  payloadOf,
  type Draft,
} from '../../lib/walk'

/*
 * The state of asking a form's fields one at a time.
 *
 * Owned by the Conversation rather than by the dock, because both halves of
 * the screen read it: the transcript prints the questions already answered as
 * bubbles, and the dock draws the one being asked now. Holding it in the dock
 * would mean the transcript asking the dock what has happened so far.
 *
 * Nothing here routes. The fields and their order come from the step, the
 * server still validates every one of them, and the only decision made is
 * which of the step's own fields to show next.
 */

/** One question already asked in this step, for the transcript. */
export interface Exchange {
  prompt: string
  answer?: string
  /** An optional question the customer chose not to answer. */
  skipped?: boolean
}

export interface FieldWalk {
  /** Empty on any step that is not a form. */
  fields: Field[]
  index: number
  /** The field being asked now. Undefined when there is nothing to walk. */
  current: Field | undefined
  /** Whether the customer may leave the current field as it stands. */
  ready: boolean
  /** The step's own prompt followed by every question answered so far. */
  exchanges: Exchange[]
  draft: Draft
  /** The server's complaint about the current field, if any. */
  error: string | undefined

  /** Set the current field's value without moving on. */
  set: (value: string) => void
  /**
   * Answer the current field and move on. Returns the finished payload when
   * that was the last field, and null when there is another to ask.
   */
  answer: (value: string) => Draft | null
  /** One field back. False when already on the first. */
  back: () => boolean
  /** Return to a field already answered, to change it. */
  jumpTo: (index: number) => void
}

interface WalkState {
  stepId: string
  index: number
  draft: Draft
  /** Errors from the last rejection, cleared per field as each is re-answered. */
  errors: Record<string, string>
  /** The rejection these errors came from, so a new one is noticed. */
  seen: string
}

const NONE: Field[] = []

function fresh(stepId: string): WalkState {
  // `seen` starts as the serialisation of no errors, so a clean step's first
  // render is not mistaken for a rejection.
  return { stepId, index: 0, draft: {}, errors: {}, seen: '{}' }
}

export function useFieldWalk(view: StepView | null): FieldWalk {
  const step = view?.step
  const fields = step?.type === 'form' ? step.fields : NONE
  const stepId = step?.id ?? ''

  const [state, setState] = useState<WalkState>(() => fresh(stepId))

  /*
   * A new step is a new walk.
   *
   * Reset during render rather than in an effect, so the first paint of a new
   * form asks its first field instead of flashing the previous step's index.
   */
  if (state.stepId !== stepId) setState(fresh(stepId))

  /*
   * A rejection lands the customer on the first field the server refused.
   *
   * The whole set was posted, so any field can come back marked, including one
   * answered five questions ago. Jumping there — rather than showing the error
   * under whichever field happens to be current — is the difference between a
   * correction and a puzzle.
   */
  const rejection = JSON.stringify(view?.errors ?? {})
  if (state.stepId === stepId && rejection !== state.seen) {
    const errors = view?.errors ?? {}
    const landing = firstError(fields, errors)
    setState((previous) => ({
      ...previous,
      errors,
      seen: rejection,
      index: landing ?? previous.index,
    }))
  }

  const current = fields[state.index]

  const exchanges = useMemo<Exchange[]>(() => {
    if (!step || step.type !== 'form') return []
    const list: Exchange[] = [{ prompt: step.prompt }]
    for (const field of fields.slice(0, state.index)) {
      const answer = echoOf(field, state.draft)
      list.push({ prompt: askOf(field), answer, skipped: answer === undefined })
    }
    return list
  }, [step, fields, state.index, state.draft])

  const set = useCallback(
    (value: string) => {
      if (!current) return
      setState((previous) => ({ ...previous, draft: { ...previous.draft, [current.name]: value } }))
    },
    [current],
  )

  const answer = useCallback(
    (value: string): Draft | null => {
      if (!current) return null
      const draft = { ...state.draft, [current.name]: value }
      if (!canLeave(current, draft)) return null

      const next = nextIndex(fields, state.index)
      // The error for this field is spent: the customer has given a new answer
      // and the server will judge that one.
      const { [current.name]: _cleared, ...errors } = state.errors

      if (next === null) {
        setState((previous) => ({ ...previous, draft, errors }))
        return payloadOf(fields, draft)
      }

      setState((previous) => ({ ...previous, draft, errors, index: next }))
      return null
    },
    [current, fields, state.draft, state.errors, state.index],
  )

  const back = useCallback((): boolean => {
    if (state.index === 0) return false
    setState((previous) => ({ ...previous, index: Math.max(0, previous.index - 1) }))
    return true
  }, [state.index])

  const jumpTo = useCallback(
    (index: number) => {
      // Only somewhere already visited. Skipping ahead would let a required
      // question go unasked, and the server would bounce the whole set.
      if (index < 0 || index > state.index) return
      setState((previous) => ({ ...previous, index }))
    },
    [state.index],
  )

  return {
    fields,
    index: state.index,
    current,
    ready: current ? canLeave(current, state.draft) : false,
    exchanges,
    draft: state.draft,
    error: current ? state.errors[current.name] : undefined,
    set,
    answer,
    back,
    jumpTo,
  }
}

/** Narrow a step to its form shape, for docks that already know it is one. */
export type FormStep = Extract<Step, { type: 'form' }>
